import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import {
    HiOutlineSparkles, HiOutlinePaperAirplane, HiOutlineRefresh, HiCheckCircle, HiOutlineShoppingBag, HiOutlineUser,
    HiOutlineTruck, HiOutlineDocumentText, HiOutlineSearch, HiOutlineExternalLink, HiX, HiOutlineExclamationCircle
} from 'react-icons/hi'
import { FaWhatsapp } from 'react-icons/fa'
import AiService from '../service/AiService'
import AuthenticationService from '../service/AuthenticationService'
import EmptyState from '../components/EmptyState'
import { formatRupee } from '../components/Price'
import { notifyCartUpdated } from '../util/cartEvents'
import brand, { whatsappLink } from '../brand'
import '../css/ai-help.css'

const CHAT_KEY = 'mv-ai-chat'
const INTRO_KEY = 'mv-ai-intro'
const MAX_CHARS = 1000
const INTRO_MS = 2200

const STARTERS = [
    { icon: <HiOutlineShoppingBag />, text: "What's in my cart?" },
    { icon: <HiOutlineSearch />, text: 'Show me party dresses for girls' },
    { icon: <HiOutlineTruck />, text: 'Where is my latest order?' },
    { icon: <HiOutlineUser />, text: 'I want to update my address' },
    { icon: <HiOutlineDocumentText />, text: 'What did I agree to in the declaration?' },
]

const PAGE_NAMES = {
    '/cart': 'your cart', '/payment': 'checkout', '/orders': 'your orders', '/profile': 'your profile', '/': 'the home page',
    '/items/all': 'all products'
}

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const load = (key, fallback) => {
    try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : fallback } catch (e) { return fallback }
}

const save = (key, value) => {
    try { sessionStorage.setItem(key, JSON.stringify(value)) } catch (e) { /* storage full or blocked: keep in memory */ }
}

const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7)

// the declaration the shopper agreed to (brand copy), as plain text for the assistant
const declarationText = () => {
    const d = brand.declaration
    if (!d) return ''
    const sections = (d.sections || []).map(s => `${s.title}:\n${(s.points || []).map(p => '- ' + p).join('\n')}`).join('\n\n')
    return [d.title, d.intro, sections].filter(Boolean).join('\n\n')
}

const firstName = () => String(AuthenticationService.getAuthUserName() || '').trim().split(/\s+/)[0]

// **bold**, "- " lists and line breaks. Other markdown some models send anyway (headings, tables, links, `code`,
// *italics*) is turned into plain text, so no stray symbols show. Everything stays text (React escapes it).
const plainInline = (text) => text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, '$1$2')
    .replace(/__([^_]+)__/g, '**$1**')

const inline = (text) => plainInline(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : part.replace(/\*\*/g, ''))

const RichText = ({ text }) => {
    const blocks = []
    let list = null
    const addItem = (value) => {
        if (!list) { list = []; blocks.push({ list }) }
        list.push(value)
    }
    String(text || '').split('\n').forEach((raw, i) => {
        const line = raw.trimEnd()
        // table rows become list items ("a · b"); separator rows and rules are dropped
        if (/^\s*\|/.test(line)) {
            if (/^[\s|:-]+$/.test(line)) return
            const cells = line.split('|').map(c => c.trim()).filter(Boolean)
            if (cells.length) addItem(cells.join(' · '))
            return
        }
        if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) return
        const item = line.match(/^\s*(?:[-•*]|\d+[.)])\s+(.*)$/)
        if (item) {
            addItem(item[1])
            return
        }
        list = null
        const heading = line.match(/^\s*#{1,6}\s+(.*)$/)
        if (heading) blocks.push({ p: '**' + heading[1].replace(/\*\*/g, '') + '**' })
        else if (line.trim()) blocks.push({ p: line })
        else blocks.push({ gap: i })
    })
    return blocks.map((b, i) => b.list ?
        <ul key={i}>{b.list.map((li, j) => <li key={j}>{inline(li)}</li>)}</ul> :
        b.p ? <p key={i}>{inline(b.p)}</p> : null)
}

// What the assistant needs to remember about its own earlier replies, sent with the history (never shown):
// a Confirm card really appeared (and what happened to it), and which products were shown, with their ids.
const ACTION_STATES = { PENDING: 'waiting for the shopper to tap', DONE: 'confirmed', CANCELLED: 'cancelled or expired' }
const historyText = (m) => {
    if (m.role !== 'assistant') return m.text
    const notes = []
    if (m.action) notes.push(`[Confirm card shown: ${m.action.summary} · ${ACTION_STATES[m.action.status] || 'shown'}]`)
    if (m.products && m.products.length) {
        notes.push(`[Products shown: ${m.products.slice(0, 8).map(p => `${p.name} (item_id ${p.itemId})`).join(', ')}]`)
    }
    return notes.length ? notes.join(' ') + '\n' + m.text : m.text
}

// one short line for a failed reply: the first sentence of the server's message
const shortError = (msg) => {
    const first = String(msg || '').trim().match(/^[^.!?]*[.!?]?/)
    return (first && first[0]) || "Couldn't answer just now."
}

// ---------- intro animation ----------

const Intro = ({ name, onDone }) => {
    useEffect(() => {
        const timer = setTimeout(onDone, INTRO_MS)
        return () => clearTimeout(timer)
    }, [onDone])
    return (
        <div className='mv-ai-intro' role='presentation'>
            <div className='mv-ai-orb' aria-hidden='true'>
                {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className={'mv-ai-spark s' + i} />)}
                <span className='mv-ai-orb-core'><HiOutlineSparkles /></span>
            </div>
            <p className='mv-ai-intro-hi'>Hi{name ? ' ' + name : ''}! 👋</p>
            <p className='mv-ai-intro-sub'>I'm {brand.name} Help. Let me get things ready for you…</p>
            <button type='button' className='mv-link-btn mv-ai-intro-skip' onClick={onDone}>Skip</button>
        </div>
    )
}

// ---------- pieces of a reply ----------

const ProductCards = ({ products }) => (
    <ul className='mv-ai-products' aria-label='Products'>
        {products.map(p =>
            <li key={p.itemId}>
                <Link to={'/shop/items/view/' + p.itemId} className='mv-ai-product'>
                    <span className='mv-ai-product-img'>{p.image && <img src={p.image} alt={p.name} loading='lazy' />}</span>
                    <span className='mv-ai-product-name'>{p.name}</span>
                    <span className='mv-ai-product-price'>
                        {formatRupee(p.price)}
                        {p.mrp && Number(p.mrp) > Number(p.price) && <s>{formatRupee(p.mrp)}</s>}
                    </span>
                </Link>
            </li>)}
    </ul>
)

const ActionCard = ({ action, busy, error, onConfirm, onCancel }) => (
    <div className={'mv-ai-action is-' + String(action.status || 'PENDING').toLowerCase()}>
        <p className='mv-ai-action-title'>
            {action.type === 'ADD_TO_CART' ? <HiOutlineShoppingBag aria-hidden='true' /> : <HiOutlineUser aria-hidden='true' />}
            {action.summary}
        </p>
        {action.details && action.details.length > 0 &&
            <ul className='mv-ai-action-details'>{action.details.map((d, i) => <li key={i}>{d}</li>)}</ul>}
        {action.status === 'PENDING' ?
            <div className='mv-ai-action-buttons'>
                <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={onCancel} disabled={busy}>Cancel</button>
                <button type='button' className='mv-btn mv-btn-primary mv-btn-sm' onClick={onConfirm} disabled={busy}>
                    {busy ? 'Saving…' : 'Confirm'}
                </button>
            </div> :
            <p className='mv-ai-action-result'>
                {action.status === 'DONE' ? <HiCheckCircle aria-hidden='true' /> : <HiX aria-hidden='true' />}
                {action.message || (action.status === 'DONE' ? 'Done.' : 'Cancelled. Nothing was changed.')}
            </p>}
        {error && <p className='mv-field-error' role='alert'>{error}</p>}
        {action.status === 'PENDING' && <p className='mv-ai-action-note'>Nothing changes until you tap Confirm.</p>}
    </div>
)

// ---------- page ----------

// AI Help: a chat assistant for the signed-in shopper (their cart, orders, profile, the declaration, finding and
// opening products). It changes the cart or profile only after the shopper taps Confirm and never pays.
// The conversation is kept in this browser tab only.
const AiHelp = () => {
    const history = useHistory()
    const name = firstName()
    const [status, setStatus] = useState(null) // { enabled, limit, remaining } | { failed }
    const [messages, setMessages] = useState(() => load(CHAT_KEY, []))
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)
    const [introDone, setIntroDone] = useState(() => reducedMotion() || load(INTRO_KEY, false) || load(CHAT_KEY, []).length > 0)
    const [actionBusy, setActionBusy] = useState(null)
    const [actionErrors, setActionErrors] = useState({})
    const listRef = useRef(null)
    const inputRef = useRef(null)
    const navTimer = useRef(null)

    useEffect(() => {
        AiService.status()
            .then(res => { setStatus(res.data.data); AiService.remember(!!res.data.data.enabled) })
            .catch(() => setStatus({ failed: true }))
        return () => clearTimeout(navTimer.current)
    }, [])

    useEffect(() => { save(CHAT_KEY, messages.slice(-60)) }, [messages])

    // keep the newest message in view
    useEffect(() => {
        const el = listRef.current
        if (!el) return
        if (messages.length === 0 && !sending) {
            el.scrollTop = 0
            return
        }
        el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion() ? 'auto' : 'smooth' })
    }, [messages, sending, introDone])

    const finishIntro = useCallback(() => {
        save(INTRO_KEY, true)
        setIntroDone(true)
    }, [])

    useEffect(() => {
        if (introDone && status && status.enabled && inputRef.current) inputRef.current.focus({ preventScroll: true })
    }, [introDone, status])

    // base: the conversation to continue (Try again drops the failed turn first)
    const send = (raw, base = messages) => {
        const text = String(raw || '').trim()
        if (!text || sending || !status || !status.enabled || status.remaining === 0) return
        const userMsg = { id: newId(), role: 'user', text: text.slice(0, MAX_CHARS) }
        const next = [...base, userMsg]
        setMessages(next)
        setInput('')
        setSending(true)
        const history_ = next.filter(m => m.text && !m.failed && (m.role === 'user' || m.role === 'assistant'))
            .slice(-20).map(m => ({ role: m.role, text: historyText(m) }))
        AiService.chat(history_, declarationText())
            .then(res => {
                const r = res.data.data || {}
                const reply = {
                    id: newId(), role: 'assistant', text: r.text || '', products: r.products || null,
                    action: r.action || null, navigate: r.navigate || null
                }
                setMessages(m => [...m, reply])
                setStatus(s => ({ ...s, remaining: r.remaining != null ? r.remaining : s.remaining }))
                if (r.action && r.action.type === 'ADD_TO_CART' && r.action.status === 'DONE') notifyCartUpdated()
                if (r.navigate) {
                    // open the page for them after a moment, so they can read the reply first
                    navTimer.current = setTimeout(() => history.push(r.navigate), 900)
                }
            })
            .catch(error => {
                const msg = error?.response?.data?.msg || (error?.response ? "Couldn't answer just now." : "Couldn't connect.")
                setMessages(m => [...m, { id: newId(), role: 'assistant', text: shortError(msg), failed: true, retry: text }])
                if (/today's AI Help messages/.test(msg)) setStatus(s => ({ ...s, remaining: 0 }))
                if (/not available right now/.test(msg)) setStatus(s => ({ ...s, enabled: false }))
            })
            .finally(() => setSending(false))
    }

    const retry = (msg) => {
        const at = messages.findIndex(x => x.id === msg.id)
        // drop the failed reply and the question it answered, then ask again
        send(msg.retry, messages.slice(0, Math.max(0, at - 1)))
    }

    const decide = (msg, confirm) => {
        setActionBusy(msg.id)
        setActionErrors(e => ({ ...e, [msg.id]: '' }))
        const call = confirm ? AiService.confirm(msg.action.id) : AiService.cancel(msg.action.id)
        call.then(res => {
            const action = res.data.data
            setMessages(m => m.map(x => x.id === msg.id ? { ...x, action: { ...x.action, ...action } } : x))
            if (confirm && action.status === 'DONE' && msg.action.type === 'ADD_TO_CART') notifyCartUpdated()
        }).catch(error => {
            const text = error?.response?.data?.msg || 'Could not do that. Please try again.'
            setActionErrors(e => ({ ...e, [msg.id]: text }))
            if (/expired|not found/i.test(text)) {
                setMessages(m => m.map(x => x.id === msg.id ? { ...x, action: { ...x.action, status: 'CANCELLED', message: text } } : x))
            }
        }).finally(() => setActionBusy(null))
    }

    const newChat = () => {
        setMessages([])
        setActionErrors({})
        if (inputRef.current) inputRef.current.focus()
    }

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            send(input)
        }
    }

    const whatsapp = whatsappLink(`Hi ${brand.name}, I need some help.`)

    if (!introDone) {
        return <main className='mv-page mv-ai'><Intro name={name} onDone={finishIntro} /></main>
    }

    if (status && (status.failed || !status.enabled) && messages.length === 0) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineSparkles />} title='AI Help is resting'
                    text={`It isn't available right now. Our team is happy to help on WhatsApp.`}
                    actionLabel='Chat on WhatsApp' onAction={() => window.open(whatsapp, '_blank', 'noopener')} />
            </main>
        )
    }

    const outOfMessages = status && status.remaining === 0
    const off = status && (status.failed || !status.enabled)

    return (
        <main className='mv-page mv-ai'>
            <section className='mv-ai-chat' aria-label={`${brand.name} Help chat`}>
                <header className='mv-ai-head'>
                    <span className='mv-ai-avatar' aria-hidden='true'><HiOutlineSparkles /></span>
                    <span className='mv-ai-head-text'>
                        <h1 className='mv-ai-title'>{brand.name} Help</h1>
                        <span className='mv-ai-sub'><span className='mv-ai-dot' aria-hidden='true' /> Orders, cart and shop help</span>
                    </span>
                    {messages.length > 0 &&
                        <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm mv-ai-new' onClick={newChat} disabled={sending}>
                            <HiOutlineRefresh aria-hidden='true' /> <span className='mv-ai-new-label'>New chat</span>
                        </button>}
                </header>

                <div className='mv-ai-list' ref={listRef} aria-live='polite' aria-busy={sending}>
                    {messages.length === 0 &&
                        <div className='mv-ai-welcome'>
                            <span className='mv-ai-welcome-orb' aria-hidden='true'><HiOutlineSparkles /></span>
                            <p className='mv-ai-welcome-hi'>Hi{name ? ' ' + name : ''}, how can I help?</p>
                            <p className='mv-ai-welcome-sub'>
                                Ask about your cart, orders, sizes or your profile. I can find and open products for you, and add
                                to your cart or update your details after you confirm.
                            </p>
                            <div className='mv-ai-starters'>
                                {STARTERS.map(s =>
                                    <button key={s.text} type='button' className='mv-ai-starter' onClick={() => send(s.text)}
                                        disabled={!status || off || outOfMessages}>
                                        <span aria-hidden='true'>{s.icon}</span>{s.text}
                                    </button>)}
                            </div>
                        </div>}

                    {messages.map(m => m.failed ?
                        // a failed reply is one small line, with Try again when it can help
                        <div key={m.id} className='mv-ai-error' role='status'>
                            <HiOutlineExclamationCircle aria-hidden='true' />
                            <span>{m.text}</span>
                            {m.retry && !outOfMessages && !off &&
                                <button type='button' className='mv-link-btn mv-ai-retry' onClick={() => retry(m)} disabled={sending}>
                                    Try again
                                </button>}
                        </div> :
                        <div key={m.id} className={'mv-ai-msg ' + (m.role === 'user' ? 'is-user' : 'is-bot')}>
                            {m.role !== 'user' && <span className='mv-ai-msg-avatar' aria-hidden='true'><HiOutlineSparkles /></span>}
                            <div className='mv-ai-msg-body'>
                                {m.text && <div className='mv-ai-bubble'><RichText text={m.text} /></div>}
                                {m.products && m.products.length > 0 && <ProductCards products={m.products} />}
                                {m.action &&
                                    <ActionCard action={m.action} busy={actionBusy === m.id} error={actionErrors[m.id]}
                                        onConfirm={() => decide(m, true)} onCancel={() => decide(m, false)} />}
                                {m.navigate &&
                                    <Link to={m.navigate} className='mv-ai-open'>
                                        <HiOutlineExternalLink aria-hidden='true' />
                                        Open {m.navigate.startsWith('/shop/items/view/') ? 'the product' :
                                            m.navigate.startsWith('/orders/view/') ? 'the order' : (PAGE_NAMES[m.navigate] || 'the page')}
                                    </Link>}
                            </div>
                        </div>)}

                    {sending &&
                        <div className='mv-ai-msg is-bot'>
                            <span className='mv-ai-msg-avatar' aria-hidden='true'><HiOutlineSparkles /></span>
                            <div className='mv-ai-bubble mv-ai-typing' aria-label='Typing'><span /><span /><span /></div>
                        </div>}
                </div>

                {outOfMessages || off ?
                    <div className='mv-ai-stopped'>
                        <p>{off ? "AI Help isn't available right now." : 'Messages refresh tomorrow.'}</p>
                        <a className='mv-btn mv-btn-secondary mv-btn-sm' href={whatsapp} target='_blank' rel='noopener noreferrer'>
                            <FaWhatsapp aria-hidden='true' /> Chat on WhatsApp
                        </a>
                    </div> :
                    <form className='mv-ai-composer' onSubmit={(e) => { e.preventDefault(); send(input) }}>
                        <label htmlFor='mv-ai-input' className='mv-visually-hidden'>Message {brand.name} Help</label>
                        <textarea id='mv-ai-input' ref={inputRef} className='mv-ai-input' rows={1} maxLength={MAX_CHARS}
                            placeholder='Ask me about the shop…' value={input} disabled={!status}
                            onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} />
                        <button type='submit' className='mv-ai-send' aria-label='Send' disabled={!input.trim() || sending || !status}>
                            <HiOutlinePaperAirplane />
                        </button>
                        <p className='mv-ai-foot'>
                            {status && status.remaining != null ? `${status.remaining} messages left today · ` : ''}
                            AI can make mistakes. It never pays for you.
                        </p>
                    </form>}
            </section>
        </main>
    )
}

export default AiHelp
